import { createBrowserClient } from '@supabase/ssr';
import { getSupabaseKey } from '@/lib/env';
import type { Database } from './types';

const SUPABASE_URL = 'https://service.canxp.ai';
const SUPABASE_PUBLISHABLE_KEY = getSupabaseKey();

const fetchWithRetry = async (url: string, options: RequestInit, retries = 3, backoff = 300): Promise<Response> => {
  try {
    return await fetch(url, options);
  } catch (error) {
    if (retries === 0) {
      throw error;
    }

    const isResourceError = String(error).includes('ERR_INSUFFICIENT_RESOURCES');
    const waitTime = isResourceError ? backoff * 2 : backoff;

    console.log(`Retrying fetch (${retries} attempts left) after ${waitTime}ms...`);
    await new Promise((resolve) => setTimeout(resolve, waitTime));

    return fetchWithRetry(url, options, retries - 1, backoff * 2);
  }
};

const activeRequests = {
  count: 0,
  max: 10,
  queue: [] as (() => void)[],

  async acquire() {
    if (this.count < this.max) {
      this.count += 1;
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.queue.push(() => resolve(true));
    });
  },

  release() {
    this.count -= 1;
    if (this.queue.length > 0 && this.count < this.max) {
      const next = this.queue.shift();
      if (next) next();
    }
  },
};

export const supabase = createBrowserClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: async (...args: Parameters<typeof fetch>) => {
      const isAttachmentRequest = args[0].toString().includes('/attachments');

      if (isAttachmentRequest) {
        await activeRequests.acquire();
      }

      try {
        const options = args[1] || {};
        const isAuthRequest = args[0].toString().includes('/auth/v1/token');
        const retries = isAuthRequest ? 5 : 3;
        const backoffTime = isAuthRequest ? 500 : 300;

        return await fetchWithRetry(args[0] as string, options as RequestInit, retries, backoffTime).catch((error) => {
          const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
          if (online) {
            console.warn('Fetch issue in Supabase client:', error);
          } else {
            console.info('Fetch skipped/failed while offline in Supabase client.');
          }
          throw error;
        });
      } finally {
        if (isAttachmentRequest) {
          activeRequests.release();
        }
      }
    },
    headers: {
      'X-Client-Info': 'CanXP AI Web SSR',
    },
  },
});

export const isOnline = () => typeof navigator !== 'undefined' && navigator.onLine === true;

export const refreshAuthToken = async () => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      console.log('No session to refresh');
      return false;
    }

    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      console.warn('Auth token refresh failed; signing out to clear invalid session:', error);
      await supabase.auth.signOut();
      return false;
    }

    if (data?.session) {
      console.log('Auth token refreshed successfully');
      return { session: data.session, user: data.session.user };
    }

    return false;
  } catch (error) {
    console.warn('Exception during token refresh; signing out to recover:', error);
    await supabase.auth.signOut();
    return false;
  }
};